// validators/restaurant.js

const restaurantValidators = {
  // Step 2 → Title
  2: ({ step1 }, t) => {
    if (!step1.name?.trim()) {
      return t("please_fill_title");
    }
    if (step1.name.trim().length > 32) {
      return t("title_too_long");
    }
    return null;
  },

  // Step 3 → Restaurant Type
  3: ({ step1 }, t) => {
    if (!step1.restaurant_type_id) {
      return t("please_select_restaurant_type");
    }
    return null;
  },

  // Step 4 → Location
  4: ({ step1 }, t) => {
    if (!step1.country_id) {
      return t("please_fill_country");
    }
    if (!step1.city_id) {
      return t("please_fill_city");
    }
    if (!step1.phone?.trim()) {
      return t("please_fill_phone");
    }
    if (!step1.address?.trim()) {
      return t("please_fill_address");
    }
    return null;
  },

  // Step 5 → Informations
  5: ({ step1 }, t) => {
    if (!step1.table_count || step1.table_count < 5 || step1.table_count > 50) {
      return t("invalid_table_count");
    }
    if (
      !step1.seating_capacity ||
      step1.seating_capacity < 10 ||
      step1.seating_capacity > 50
    ) {
      return t("invalid_seating_capacity");
    }
    if (
      step1.floor_number === undefined ||
      step1.floor_number === null ||
      step1.floor_number < 0 ||
      step1.floor_number > 5
    ) {
      return t("invalid_floor_number");
    }
    return null;
  },

  // Step 7 → Description
  7: ({ step2 }, t) => {
    if (!step2.short_summary?.trim()) {
      return t("please_fill_summary");
    }
    if (step2.short_summary.trim().length < 32) {
      return t("summary_too_short");
    }
    if (step2.short_summary.trim().length > 200) {
      return t("summary_too_long");
    }
    if (!step2.detailed_description?.trim()) {
      return t("please_fill_description");
    }
    if (step2.detailed_description.trim().length < 60) {
      return t("description_too_short");
    }
    if (step2.detailed_description.trim().length > 500) {
      return t("description_too_long");
    }
    if (!step2.key_points.length) {
      return t("please_fill_key_points");
    }
    return null;
  },

  // Step 8 → Meals
  8: ({ step2 }, t) => {
    if (
      !step2.meal_type ||
      !Array.isArray(step2.meal_type) ||
      step2.meal_type.length === 0
    ) {
      return t("please_select_at_least_one_meal_type");
    }
    return null;
  },

  // Step 9 → Services
  9: ({ step2 }, t) => {
    if (step2.has_service) {
      if (
        !step2.restaurant_service ||
        !Array.isArray(step2.restaurant_service) ||
        step2.restaurant_service.length === 0
      ) {
        return t("please_select_at_least_one_restaurant_service");
      }
    }
    return null;
  },

  // Step 11 → Menu
  11: ({ step3 }, t) => {
    if (
      !step3.dishes ||
      !Array.isArray(step3.dishes) ||
      step3.dishes.length === 0
    ) {
      return t("please_add_at_least_one_dish");
    }

    // Validate each dish
    for (let i = 0; i < step3.dishes.length; i++) {
      const dish = step3.dishes[i];
      const dishLabel = `${t("dish")} #${i + 1}`;

      if (!dish.meal_type_id) {
        return t("dish_missing_meal_type", { dish: dishLabel });
      }

      if (!dish.dish_name?.trim()) {
        return t("dish_missing_name", { dish: dishLabel });
      }

      if (!dish.dish_description?.trim()) {
        return t("dish_missing_description", { dish: dishLabel });
      }

      if (!dish.dish_price || dish.dish_price <= 0) {
        return t("dish_missing_price", { dish: dishLabel });
      }
    }

    return null;
  },

// Step 12 → Working Hours / Schedules
12: ({ step3 }, t) => {
  if (
    !step3.schedules ||
    !Array.isArray(step3.schedules) ||
    step3.schedules.length === 0
  ) {
    return t("please_add_working_hours");
  }

  const dayNames = {
    monday: t("monday"),
    tuesday: t("tuesday"),
    wednesday: t("wednesday"),
    thursday: t("thursday"),
    friday: t("friday"),
    saturday: t("saturday"),
    sunday: t("sunday"),
  };

  for (const schedule of step3.schedules) {
    // Missing day
    if (!schedule.day_of_week) {
      return t("schedule_missing_day");
    }

    const dayLabel = dayNames[schedule.day_of_week];
    
    // Invalid day string
    if (!dayLabel) {
      return t("schedule_missing_day");
    }

    // Validate times only if not closed and not 24h
    if (!schedule.is_closed && !schedule.is_open_24h) {
      if (!schedule.start_time) {
        return t("schedule_missing_start_time", { schedule: dayLabel });
      }

      if (!schedule.end_time) {
        return t("schedule_missing_end_time", { schedule: dayLabel });
      }

      if (schedule.start_time >= schedule.end_time) {
        return t("schedule_end_must_be_after_start", {
          schedule: dayLabel,
        });
      }
    }
  }

  return null;
},

  // Step 13 → Policy
  13: ({ step3 }, t) => {
    // Validate dates
    if (!step3.start_date) {
      return t("please_fill_start_date");
    }

    if (!step3.end_date) {
      return t("please_fill_end_date");
    }

    // Validate date range
    const startDate = new Date(step3.start_date);
    const endDate = new Date(step3.end_date);

    if (startDate >= endDate) {
      return t("end_date_must_be_after_start_date");
    }

    // Validate cancellation policy
    if (
      step3.cancellation_policy_id === undefined ||
      step3.cancellation_policy_id === null
    ) {
      return t("please_fill_cancellation_policy_id");
    }

    return null;
  },

  // Step 14 → Booking
  14: ({ step3 }, t) => {
    if (!step3.booking_confirmation_mode) {
      return t("please_fill_booking_confirmation_mode");
    }

    if (
      step3.booking_confirmation_mode !== "instant" &&
      step3.booking_confirmation_mode !== "manual"
    ) {
      return t("invalid_booking_confirmation_mode");
    }

    return null;
  },

  // Step 15 → Review (Final validation before submission)
  15: ({ step1, step2, step3 }, t) => {
    // Validate step1 fields
    if (!step1.name?.trim()) {
      return t("please_fill_title_before_submit");
    }

    if (!step1.restaurant_type_id) {
      return t("please_select_restaurant_type_before_submit");
    }

    if (!step1.country_id) {
      return t("please_fill_country_before_submit");
    }

    if (!step1.city_id) {
      return t("please_fill_city_before_submit");
    }

    // Validate step2 fields
    if (!step2.short_summary?.trim()) {
      return t("please_fill_summary_before_submit");
    }

    if (!step2.detailed_description?.trim()) {
      return t("please_fill_description_before_submit");
    }

    if (!step2.meal_type || step2.meal_type.length === 0) {
      return t("please_select_meal_types_before_submit");
    }

    // Validate step3 fields
    if (!step3.dishes || step3.dishes.length === 0) {
      return t("please_add_dishes_before_submit");
    }

    if (!step3.schedules || step3.schedules.length === 0) {
      return t("please_add_working_hours_before_submit");
    }

    if (!step3.start_date) {
      return t("please_fill_start_date_before_submit");
    }

    if (!step3.end_date) {
      return t("please_fill_end_date_before_submit");
    }

    if (
      step3.cancellation_policy_id === undefined ||
      step3.cancellation_policy_id === null
    ) {
      return t("please_fill_cancellation_policy_before_submit");
    }

    if (!step3.booking_confirmation_mode) {
      return t("please_fill_booking_confirmation_mode_before_submit");
    }

    return null;
  },

  // No shared validators - everything is custom
  shared: [],
};

export default restaurantValidators;
